import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LevelPromoBannerSlide } from '@/components/banners/LevelPromoBannerSlide';
import { RewardsStoreBannerSlide } from '@/components/banners/RewardsStoreBannerSlide';
import { PointsRequestBannerSlide } from '@/components/banners/PointsRequestBannerSlide';
import { ReferralBannerSlide } from '@/components/banners/ReferralBannerSlide';

interface PromoBannersCarouselProps {
    onActionClick: (index: number) => void;
    isLoggedIn: boolean;
}

export const PromoBannersCarousel: React.FC<PromoBannersCarouselProps> = ({ onActionClick, isLoggedIn }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const [touchEndX, setTouchEndX] = useState<number | null>(null);
    const totalSlides = 4;

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % totalSlides);
        }, 8000);
        return () => clearInterval(interval);
    }, [totalSlides, currentSlide]);

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentSlide(prev => (prev + 1) % totalSlides);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchEndX(null);
        setTouchStartX(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEndX(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStartX || !touchEndX) return;
        const distance = touchStartX - touchEndX;
        const swipeThreshold = 50;

        if (distance > swipeThreshold) {
            // Swiped left, go to next
            setCurrentSlide(prev => (prev + 1) % totalSlides);
        } else if (distance < -swipeThreshold) {
            // Swiped right, go to previous
            setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
        }
    };

    return (
        <div className="w-full max-w-4xl mx-auto mb-10 relative">

            {/* Slide Container */}
            <div
                className="relative w-full h-auto overflow-hidden rounded-[32px] group cursor-pointer"
                onClick={() => onActionClick(currentSlide)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >

                {/* Background Animations for active slide */}
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-fuchsia-600 blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200 animate-tilt"></div>

                <div className="relative glass-card p-6 md:p-8 flex flex-col items-center gap-6 border border-white/20 dark:border-white/5 transition-all">
                    {/* Decorative elements shared across slides */}
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-2xl pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-gradient-to-tr from-fuchsia-500/20 to-pink-500/20 rounded-full blur-xl pointer-events-none"></div>

                    {/* Navigation Arrows */}
                    <button
                        onClick={handlePrev}
                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-50 p-1.5 md:p-2 rounded-full bg-white/20 dark:bg-gray-900/40 text-gray-900 dark:text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/40 dark:hover:bg-gray-900/60 shadow-lg"
                        aria-label="Previous slide"
                    >
                        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-50 p-1.5 md:p-2 rounded-full bg-white/20 dark:bg-gray-900/40 text-gray-900 dark:text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/40 dark:hover:bg-gray-900/60 shadow-lg"
                        aria-label="Next slide"
                    >
                        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                    </button>

                    {/* Slides Wrapper */}
                    <div
                        className="flex w-full transition-transform duration-700 ease-in-out h-full"
                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                        <LevelPromoBannerSlide isLoggedIn={isLoggedIn} />

                        <RewardsStoreBannerSlide isLoggedIn={isLoggedIn} />

                        <PointsRequestBannerSlide isLoggedIn={isLoggedIn} />

                        <ReferralBannerSlide isLoggedIn={isLoggedIn} />
                    </div>
                </div>
            </div>

            {/* Dots Nav */}
            <div className="flex justify-center mt-4 gap-2">
                {[...Array(totalSlides)].map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`transition-all duration-300 rounded-full h-2 ${currentSlide === idx ? 'w-6 bg-blue-500 dark:bg-blue-400' : 'w-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'}`}
                        aria-label={`Slide ${idx + 1}`}
                    />
                ))}
            </div>

        </div>
    );
};
